import { FC, Fragment } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { DEFAULT_SECTION } from './ManageCustomPage';
import { ICustomPageSection, SectionType } from '../../../models';
import { createCustomPageImage } from '../../../fetch';
import { ImageUpload } from '../../../components';
import { getRandomNegativeNumber } from '../../../helpers';
import { Add, Clear } from '@mui/icons-material';
import { Divider, Grid, Button, TextField, Typography } from '@mui/material';

interface IMobileAppSection {
  setSections: (val: ICustomPageSection[]) => void;
  parentSections: ICustomPageSection[];
  parentIndex: number;
  sections: ICustomPageSection[];
  index: number;
}

export const MobileAppSection: FC<IMobileAppSection> = ({ index, sections, parentSections, setSections, parentIndex }) => {
  // use this to deeply copy sections so we don't modify the original array
  const copy: ICustomPageSection[] = JSON.parse(JSON.stringify(parentSections));
  const classes = useStyles();
  return (
    <div>
      <Typography variant="h6">Mobile App Section</Typography>
      <Typography variant="caption" component="p" paragraph>
        Add mobile app headings along with images and mobile links
      </Typography>
      <Grid container spacing={1}>
        {sections.map((item, subSectionIndex: number) => {
          const mobileItem = copy[parentIndex].sections[index].sections[subSectionIndex];
          return (
            <Fragment key={`${subSectionIndex}`}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="App Title"
                  autoComplete="nope"
                  variant="outlined"
                  value={item.text}
                  fullWidth
                  sx={{ marginBottom: '0.75rem' }}
                  onChange={e => {
                    mobileItem.text = e.target.value;
                    setSections(copy);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ImageUpload
                  labelClassName={classes.labelClassName}
                  handleFileChange={async (_, file) => {
                    // remove image
                    if (!file) {
                      mobileItem.imageUrl = '';
                    } else {
                      const formData = new FormData();
                      formData.append('File', file);
                      const imageUrl = await createCustomPageImage(formData);
                      mobileItem.imageUrl = imageUrl;
                    }
                    setSections(copy);
                  }}
                  imageKey={`${subSectionIndex}`}
                  image={item.imageUrl}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="iOS App Store Link"
                  autoComplete="nope"
                  variant="outlined"
                  value={item.sections?.[0]?.linkUrl}
                  fullWidth
                  onChange={e => {
                    if (mobileItem?.sections?.[0]?.linkUrl) {
                      mobileItem.sections[0].linkUrl = e.target.value;
                      // else statement only when the parent level is created, but both child sections are empty
                      // create both child sections on the fly
                    } else {
                      mobileItem.sections[0] = {
                        ...DEFAULT_SECTION,
                        linkUrl: e.target.value,
                        name: 'iOS App Link',
                        type: SectionType.Mobile_App_Section,
                        sortOrder: 1
                      };
                      mobileItem.sections[1] = {
                        ...DEFAULT_SECTION,
                        linkUrl: mobileItem?.sections?.[1]?.linkUrl || '',
                        name: 'Google App Link',
                        type: SectionType.Mobile_App_Section,
                        sortOrder: 2
                      };
                    }
                    setSections(copy);
                  }}
                  error={
                    // not a valid relative route
                    (mobileItem?.sections?.[0]?.linkUrl && !mobileItem?.sections?.[0]?.linkUrl?.startsWith('http')) ||
                    // can't contain white space
                    mobileItem?.sections?.[0]?.linkUrl?.indexOf(' ') >= 0
                      ? true
                      : false
                  }
                  helperText={
                    mobileItem?.sections?.[0]?.linkUrl && !mobileItem?.sections?.[0]?.linkUrl?.startsWith('http')
                      ? 'Must start with http'
                      : mobileItem?.sections?.[0]?.linkUrl?.indexOf(' ') >= 0
                      ? 'Must not contain white space'
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="Google App Store Link"
                  autoComplete="nope"
                  variant="outlined"
                  value={item.sections?.[1]?.linkUrl}
                  fullWidth
                  onChange={e => {
                    if (mobileItem?.sections?.[1]?.linkUrl) {
                      mobileItem.sections[1].linkUrl = e.target.value;
                      // else statement only when the parent level is created, but both child sections are empty
                      // create both child sections on the fly
                    } else {
                      mobileItem.sections[0] = {
                        ...DEFAULT_SECTION,
                        linkUrl: mobileItem?.sections?.[0]?.linkUrl || '',
                        name: 'iOS App Link',
                        type: SectionType.Mobile_App_Section,
                        sortOrder: 1
                      };
                      mobileItem.sections[1] = {
                        ...DEFAULT_SECTION,
                        linkUrl: e.target.value,
                        name: 'Google App Link',
                        type: SectionType.Mobile_App_Section,
                        sortOrder: 2
                      };
                    }
                    setSections(copy);
                  }}
                  error={
                    // not a valid relative route
                    (mobileItem?.sections?.[1]?.linkUrl && !mobileItem?.sections?.[1]?.linkUrl?.startsWith('http')) ||
                    // can't contain white space
                    mobileItem?.sections?.[1]?.linkUrl?.indexOf(' ') >= 0
                      ? true
                      : false
                  }
                  helperText={
                    mobileItem?.sections?.[1]?.linkUrl && !mobileItem?.sections?.[1]?.linkUrl?.startsWith('http')
                      ? 'Must start with http'
                      : mobileItem?.sections?.[1]?.linkUrl?.indexOf(' ') >= 0
                      ? 'Must not contain white space'
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="error"
                  title="Remove"
                  startIcon={<Clear />}
                  onClick={() => {
                    const result = window.confirm('Are you sure you want to remove this mobile app item?');
                    if (result) {
                      const newItems = copy[parentIndex].sections[index].sections.filter(l => l.customPageSectionId !== item.customPageSectionId);
                      copy[parentIndex].sections[index].sections = newItems;
                      setSections(copy);
                    }
                  }}
                >
                  Remove Mobile App Item
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Fragment>
          );
        })}
      </Grid>

      <Button
        color="secondary"
        startIcon={<Add />}
        sx={{ marginTop: '1rem', padding: 0 }}
        onClick={() => {
          const randomNumber = getRandomNegativeNumber();
          copy[parentIndex].sections[index].sections.push({
            ...DEFAULT_SECTION,
            text: 'Mobile App Placeholder',
            customPageSectionId: randomNumber,
            type: SectionType.Mobile_App_Section,
            sortOrder: copy[parentIndex].sections[index].sections.length + 1,
            sections: [
              {
                ...DEFAULT_SECTION,
                linkUrl: '',
                name: 'iOS App Link',
                type: SectionType.Mobile_App_Section,
                sortOrder: 1
              },
              {
                ...DEFAULT_SECTION,
                linkUrl: '',
                name: 'Google App Link',
                type: SectionType.Mobile_App_Section,
                sortOrder: 2
              }
            ]
          });
          setSections(copy);
        }}
      >
        Add Mobile App Item
      </Button>
    </div>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  labelClassName: {
    marginTop: 0
  }
}));
